import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createLoveShot } from '../../../store/slices/loveshotsSlice';
import { FaTimes, FaImage, FaVideo, FaMapMarkerAlt, FaSpinner, FaSmile } from 'react-icons/fa';
import './CreateLoveShot.css';

const emojis = ['😀', '😂', '😍', '🥰', '😘', '😉', '😎', '🤔', '😢', '😭', '😤', '😡', '🥺', '😴', '🤤', '🤗', '🤭', '🤫', '🤥', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '🥱', '😴', '😌', '😛', '😜', '😝', '🤤', '🤗', '🤭', '🤫', '🤥', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '🥱', '😴', '😌', '😛', '😜', '😝'];

const CreateLoveShot = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setVideo(null);
      setVideoPreview(null);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideo(file);
      setImage(null);
      setImagePreview(null);
      const reader = new FileReader();
      reader.onload = () => setVideoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setLocation('Localisation actuelle'); // Or use reverse geocoding
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Impossible d\'obtenir la localisation. Veuillez vérifier les permissions.');
        }
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par ce navigateur.');
    }
  };

  const onEmojiClick = (emojiData) => {
    setContent((prevContent) => prevContent + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !image && !video) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('caption', content);
    if (image) formData.append('image', image);
    if (video) formData.append('video', video);
    formData.append('location', location);
    if (latitude) formData.append('latitude', latitude);
    if (longitude) formData.append('longitude', longitude);
    formData.append('is_public', isPublic ? '1' : '0');

    try {
      await dispatch(createLoveShot(formData));
      handleClose();
    } catch (error) {
      console.error('Error creating LoveShot:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContent('');
    setImage(null);
    setVideo(null);
    setLocation('');
    setLatitude(null);
    setLongitude(null);
    setIsPublic(true);
    setImagePreview(null);
    setVideoPreview(null);
    setShowEmojiPicker(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="create-loveshot-modal-overlay" onClick={handleClose}>
      <div className="create-loveshot-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Créer un LoveShot</h2>
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-loveshot-form">
          <div className="content-section">
            <div className="textarea-container">
              <textarea
                placeholder="Qu'est-ce qui vous rend heureux aujourd'hui ? 💕"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={500}
                rows={4}
              />
              <button
                type="button"
                className="emoji-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <FaSmile />
              </button>
            </div>
            {showEmojiPicker && (
              <div className="emoji-picker-container">
                <div className="emoji-grid">
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      className="emoji-item"
                      onClick={() => {
                        setContent((prevContent) => prevContent + emoji);
                        setShowEmojiPicker(false);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="char-count">{content.length}/500</div>
          </div>

          <div className="media-section">
            <div className="media-buttons">
              <label className="media-btn">
                <FaImage />
                Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              <label className="media-btn">
                <FaVideo />
                Vidéo
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  style={{ display: 'none' }}
                />
              </label>
              <button type="button" className="media-btn" onClick={handleGetLocation}>
                <FaMapMarkerAlt />
                Localisation
              </button>
            </div>

            {imagePreview && (
              <div className="media-preview">
                <img src={imagePreview} alt="Preview" />
                <button type="button" onClick={() => { setImage(null); setImagePreview(null); }}>
                  <FaTimes />
                </button>
              </div>
            )}

            {videoPreview && (
              <div className="media-preview">
                <video src={videoPreview} controls />
                <button type="button" onClick={() => { setVideo(null); setVideoPreview(null); }}>
                  <FaTimes />
                </button>
              </div>
            )}
          </div>



          <div className="privacy-section">
            <label className="privacy-toggle">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              Public
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClose} className="cancel-btn">
              Annuler
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || (!content.trim() && !image && !video)}
            >
              {loading ? <FaSpinner className="spinner" /> : 'Publier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLoveShot;
